import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
  Badge,
  Checkbox,
} from "@shopify/polaris";
import { SearchIcon, RefreshIcon } from "@shopify/polaris-icons";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utility/funcs";
import { formatDuration } from "./trasladoStatus";
import ClabeBadge from "../Services/ClabeBadge";
import { quoteComparison } from "./trasladoDiff";
import { tagOptions } from "../../utility/pase";

// New traslado: pick the unit, type origin and destination CPs, and the cost is
// quoted immediately.
//   km, drive time, casetas  -> traslado_quote edge function (Google, cached per CP pair)
//   chofer, gasolina, total  -> compute_traslado_estimate (model $/km, km/L, fuel price)
//   regreso                  -> typed by staff: bus, plane, or $0 when the driver
//                               comes back driving another traslado. Not
//                               estimated, and never compared to a suggestion.
//   comida                   -> suggest_traslado_comida by drive time, editable
// Km are NOT editable: they always come from Google for the CP pair. Casetas,
// regreso and comida are editable; what was used and what was suggested are
// both frozen into traslado_orders.estimate so any hand change is visible.
//
// Also the edit screen (/vehicle_transfers/:trasladoId/edit), allowed only
// before the trip starts (Cotización / Autorizado): once it is under way the
// quote is the baseline the real cost is compared against.

const CP_RE = /^\d{5}$/;
const CAR_SELECT =
  "id, plate, vin, year, model(id, name, image_url, km_per_liter, km_per_liter_estimated, traslado_mxn_per_km, brand(name), fuel:fuel_type(name))";
export const EDITABLE_STATUSES = ["draft", "authorized"];
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

async function invokeError(error) {
  try {
    const body = await error.context.json();
    return body?.error || error.message;
  } catch {
    return error.message;
  }
}

function NewTraslado() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();
  const { trasladoId } = useParams();
  const editing = !!trasladoId;
  const [original, setOriginal] = useState(null); // order being edited
  const [loadError, setLoadError] = useState(null);
  // Saved price inputs to put back after the route is re-fetched on load,
  // instead of the fresh suggestions.
  const restoreRef = useRef(null);

  const [plate, setPlate] = useState("");
  const [car, setCar] = useState(null);
  const [carError, setCarError] = useState(null);
  const [carLoading, setCarLoading] = useState(false);

  const [originCp, setOriginCp] = useState("");
  const [destCp, setDestCp] = useState("");
  const [route, setRoute] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState(null);

  // Editable price inputs (strings, as typed).
  const [km, setKm] = useState("");
  const [toll, setToll] = useState("");
  const [returnFare, setReturnFare] = useState("");
  const [comida, setComida] = useState("0");

  const [estimate, setEstimate] = useState(null);
  const [estimateError, setEstimateError] = useState(null);
  // The same estimate priced only with what the system suggests for this
  // route, to show and save what was changed by hand.
  const [suggestedEst, setSuggestedEst] = useState(null);

  const [trasladistas, setTrasladistas] = useState([]);
  const [trasladistaId, setTrasladistaId] = useState("");
  // Optional second trasladista (backup / manager with a fee): receives only
  // `secondaryAmount` out of the chofer pay; the main one gets the rest plus
  // comida and every ticket reimbursement.
  const [secondaryId, setSecondaryId] = useState("");
  const [secondaryAmount, setSecondaryAmount] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  // TAG PASE the car takes (optional); Casetas TAG uses it to link its tolls.
  const [tags, setTags] = useState([]);
  const [paseTag, setPaseTag] = useState("");
  const [notes, setNotes] = useState("");
  const [cobroCliente, setCobroCliente] = useState(false);
  const [precioCliente, setPrecioCliente] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const searchCar = async (e) => {
    e?.preventDefault();
    const q = plate.trim().toUpperCase();
    if (!q) return;
    setCarLoading(true);
    setCarError(null);
    const { data } = await supabase
      .from("cars")
      .select(CAR_SELECT)
      .or(`plate.eq.${q},vin.eq.${q}`)
      .limit(1);
    setCarLoading(false);
    if (data?.length) setCar(data[0]);
    else setCarError("No se encontró la placa o VIN en la flotilla.");
  };

  const fetchRoute = async (refresh = false) => {
    setRouteLoading(true);
    setRouteError(null);
    const { data, error } = await supabase.functions.invoke("traslado_quote", {
      body: { origin_cp: originCp, dest_cp: destCp, refresh },
    });
    setRouteLoading(false);
    if (error) {
      setRoute(null);
      setRouteError(await invokeError(error));
      return;
    }
    setRoute(data);
    const saved = restoreRef.current;
    restoreRef.current = null;
    if (saved) {
      // Km are never taken from the saved quote: they always come from the route.
      setKm(String(data.km));
      setToll(saved.toll);
      setReturnFare(saved.returnFare);
      setComida(saved.comida);
      return;
    }
    setKm(String(data.km));
    setToll(data.toll_mxn != null ? String(data.toll_mxn) : "");
    setComida(data.comida_suggested != null ? String(data.comida_suggested) : "0");
  };

  // Edit mode: load the order and fill every field from it.
  useEffect(() => {
    if (!editing) return;
    (async () => {
      const { data: o, error } = await supabase
        .from("traslado_orders")
        .select(`*, car(${CAR_SELECT})`)
        .eq("id", trasladoId)
        .single();
      if (error || !o) {
        setLoadError(error?.message || "Traslado no encontrado");
        return;
      }
      const { count: paid } = await supabase
        .from("traslado_settlements")
        .select("id", { count: "exact", head: true })
        .eq("traslado_order", o.id)
        .neq("status", "cancelled");
      if (paid) {
        setLoadError("Ya se pagó el anticipo de este traslado; la cotización ya no se puede editar.");
        return;
      }
      setOriginal(o);
      const e = o.estimate || {};
      const str = (v) => (v == null ? "" : String(v));
      restoreRef.current = {
        km: str(o.planned_km ?? e.km),
        toll: str(e.tolls),
        returnFare: str(e.return_fare),
        comida: str(e.comida ?? 0),
      };
      setCar(o.car);
      setTrasladistaId(o.trasladista ? String(o.trasladista) : "");
      setSecondaryId(o.trasladista_secondary ? String(o.trasladista_secondary) : "");
      setSecondaryAmount(o.secondary_amount != null ? String(o.secondary_amount) : "");
      setScheduledAt(toLocalInput(o.scheduled_at));
      setPaseTag(o.pase_tag || "");
      setNotes(o.notes || "");
      setCobroCliente(!!o.cobro_cliente);
      setPrecioCliente(o.precio_cliente != null ? String(o.precio_cliente) : "");
      setOriginCp(o.origin_cp || "");
      setDestCp(o.dest_cp || "");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trasladoId]);

  useEffect(() => {
    Promise.all([
      supabase.from("trasladistas").select("id, name, license_expires").eq("active", true).order("name"),
      supabase.from("trasladistas_bank_status").select("trasladista_id, clabe_status, holder_name"),
      supabase.from("pase_tags").select("*").eq("active", true).order("name"),
    ]).then(([{ data: people }, { data: statuses }, { data: paseTags }]) => {
      const byId = Object.fromEntries((statuses || []).map((x) => [x.trasladista_id, x]));
      setTrasladistas((people || []).map((p) => ({ ...p, bank: byId[p.id] || {} })));
      setTags(paseTags || []);
    });
  }, []);

  // Quote as soon as both CPs are complete.
  useEffect(() => {
    setRoute(null);
    setEstimate(null);
    if (!CP_RE.test(originCp) || !CP_RE.test(destCp)) return;
    if (originCp === destCp) {
      setRouteError("Origen y destino son el mismo código postal.");
      return;
    }
    const t = setTimeout(() => fetchRoute(false), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originCp, destCp]);

  // Re-price whenever the unit or any input changes.
  useEffect(() => {
    if (!car || !route) {
      setEstimate(null);
      return;
    }
    const kmN = Number(km);
    if (!(kmN > 0)) {
      setEstimate(null);
      return;
    }
    const num = (v) => (String(v).trim() === "" ? null : Number(v));
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc("compute_traslado_estimate", {
        p_car: car.id,
        p_km: Math.round(kmN),
        p_toll: num(toll),
        p_return_fare: num(returnFare),
        p_comida: num(comida) ?? 0,
      });
      if (error) {
        setEstimate(null);
        setEstimateError(
          /No traslado rate/.test(error.message)
            ? "El modelo de esta unidad no tiene tarifa de chofer por km."
            : error.message,
        );
      } else {
        setEstimateError(null);
        setEstimate(data);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [car, route, km, toll, returnFare, comida]);

  useEffect(() => {
    if (!car || !route) {
      setSuggestedEst(null);
      return;
    }
    supabase
      .rpc("compute_traslado_estimate", {
        p_car: car.id,
        p_km: route.km,
        p_toll: route.toll_mxn,
        // Regreso has no suggestion: use the quote's own, so it never counts as a change.
        p_return_fare: returnFare === "" ? null : Number(returnFare),
        p_comida: route.comida_suggested ?? 0,
      })
      .then(({ data }) => setSuggestedEst(data || null));
  }, [car, route, returnFare]);

  const save = async (status) => {
    setSaving(true);
    setSaveError(null);
    const precio = cobroCliente && precioCliente !== "" ? Number(precioCliente) : null;
    const fields = {
        car: car.id,
        origin_cp: originCp,
        dest_cp: destCp,
        origin_label: route.origin_label,
        dest_label: route.dest_label,
        planned_km: route.km,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        pase_tag: paseTag || null,
        trasladista: trasladistaId ? Number(trasladistaId) : null,
        trasladista_secondary: trasladistaId && secondaryId ? Number(secondaryId) : null,
        secondary_amount:
          trasladistaId && secondaryId && secondaryAmount !== "" ? Number(secondaryAmount) : null,
        driver_rate: estimate.rate_per_km,
        suggested_extra: estimate.suggested_extra,
        // What we agree to pay the driver; starts at the rate card.
        driver_extra: estimate.suggested_extra,
        cobro_cliente: cobroCliente,
        precio_cliente: precio,
        estimate: {
          ...estimate,
          suggested: suggestedEst
            ? {
                km: suggestedEst.km,
                suggested_extra: suggestedEst.suggested_extra,
                fuel_cost: suggestedEst.fuel_cost,
                tolls: route.toll_mxn,
                return_fare: suggestedEst.return_fare,
                comida: suggestedEst.comida,
                total: suggestedEst.total,
              }
            : null,
          route: {
            km_google: route.km,
            duration_min: route.duration_min,
            toll_google: route.toll_mxn,
            toll_known: route.toll_known,
            source: route.source,
            fetched_at: route.fetched_at,
          },
        },
        estimated_total: estimate.total,
        notes: notes.trim() || null,
    };

    if (editing) {
      // A driver payment someone negotiated by hand is kept; one that was just
      // the rate card follows the new quote.
      if (
        original.driver_extra != null &&
        Number(original.driver_extra) !== Number(original.suggested_extra)
      )
        delete fields.driver_extra;
      const { error } = await supabase
        .from("traslado_orders")
        .update(fields)
        .eq("id", original.id)
        .in("status", EDITABLE_STATUSES);
      setSaving(false);
      if (error) {
        setSaveError(error.message);
        return;
      }
      navigate(`/vehicle_transfers/${original.id}`);
      return;
    }

    const { data, error } = await supabase
      .from("traslado_orders")
      .insert({ ...fields, status })
      .select("id")
      .single();
    setSaving(false);
    if (error) {
      setSaveError(error.message);
      return;
    }
    navigate(`/vehicle_transfers/${data.id}`);
  };

  const trasladista = trasladistas.find((t) => String(t.id) === trasladistaId);
  const licenseExpired =
    trasladista?.license_expires && new Date(trasladista.license_expires) < new Date();
  const carTitle = car
    ? [car.model?.brand?.name, car.model?.name, car.year].filter(Boolean).join(" ")
    : "";
  const tollEdited = route && route.toll_mxn != null && Number(toll) !== route.toll_mxn;

  // Live comparison against the suggestion (chofer compared at the rate card
  // here; the negotiated payment is compared on the traslado page).
  const cmp =
    estimate && suggestedEst
      ? quoteComparison({
          ...estimate,
          suggested: { ...suggestedEst, tolls: route?.toll_mxn },
        })
      : null;
  const cmpRow = (key) => cmp?.rows.find((r) => r.key === key);

  const line = (label, value, hint, key) => {
    const r = key ? cmpRow(key) : null;
    return (
      <InlineStack align="space-between" blockAlign="start" wrap={false}>
        <BlockStack gap="050">
          <InlineStack gap="150" blockAlign="center">
            <Text>{label}</Text>
            {r?.changed && <Badge tone="attention" size="small">Modificado</Badge>}
          </InlineStack>
          {hint && (
            <Text tone="subdued" variant="bodySm">
              {hint}
            </Text>
          )}
          {r?.changed && (
            <Text tone="caution" variant="bodySm">
              {`Sugerido ${formatPrice(r.suggested)} (${r.diff > 0 ? "+" : ""}${formatPrice(r.diff)})`}
            </Text>
          )}
        </BlockStack>
        <Text alignment="end">{value}</Text>
      </InlineStack>
    );
  };

  const estimateCard = () => {
    if (!car || !route)
      return (
        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd">Costo estimado</Text>
            <Text tone="subdued">
              Selecciona la unidad y escribe los dos códigos postales.
            </Text>
          </BlockStack>
        </Card>
      );
    if (estimateError)
      return (
        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd">Costo estimado</Text>
            <Banner tone="critical">
              {estimateError}{" "}
              <Link url="/traslado_rates">Configurar en Tarifas de Traslado</Link>
            </Banner>
          </BlockStack>
        </Card>
      );
    if (!estimate)
      return (
        <Card>
          <Spinner size="small" />
        </Card>
      );

    const missing = estimate.missing || [];
    const e = estimate;
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Costo estimado</Text>
            {cmp?.adjusted && <Badge tone="attention">Ajustado vs sugerido</Badge>}
          </InlineStack>
          {line(
            "Chofer",
            formatPrice(e.suggested_extra),
            `${e.km} km × ${formatPrice(e.rate_per_km)}/km`,
            "chofer",
          )}
          {line(
            "Gasolina",
            e.fuel_cost != null ? formatPrice(e.fuel_cost) : "—",
            e.fuel_cost != null
              ? `${e.fuel_liters} L × ${formatPrice(e.fuel_price)}/L${
                  car.model?.km_per_liter_estimated ? " · km/L estimado" : ""
                }`
              : missing.includes("fuel_price")
                ? "Falta el precio del combustible"
                : "Falta el rendimiento (km/L) del modelo",
            "gasolina",
          )}
          {line("Casetas", toll === "" ? "—" : formatPrice(e.tolls), toll === "" ? "Sin precio" : null, "casetas")}
          {line(
            "Regreso del chofer",
            returnFare === "" ? "—" : formatPrice(e.return_fare),
            returnFare === "" ? "Por capturar" : null,
          )}
          {line("Comida", formatPrice(e.comida), null, "comida")}
          <Divider />
          <InlineStack align="space-between">
            <Text variant="headingMd">{e.complete ? "Total" : "Total (mínimo)"}</Text>
            <Text variant="headingLg">{formatPrice(e.total)}</Text>
          </InlineStack>
          {cmpRow("total")?.changed && (
            <Text tone="caution" variant="bodySm" alignment="end">
              {`Sugerido ${formatPrice(cmpRow("total").suggested)} (${cmpRow("total").diff > 0 ? "+" : ""}${formatPrice(cmpRow("total").diff)})`}
            </Text>
          )}
          <Divider />
          <Checkbox
            label="Con cobro a cliente"
            helpText="El traslado se le cobra al cliente (p. ej. entrega en su ciudad)."
            checked={cobroCliente}
            onChange={(v) => {
              setCobroCliente(v);
              if (!v) setPrecioCliente("");
            }}
          />
          {cobroCliente && (
            <BlockStack gap="100">
              <TextField
                label="Precio al cliente"
                type="number"
                prefix="$"
                autoComplete="off"
                value={precioCliente}
                onChange={setPrecioCliente}
                helpText="Opcional; para ver el margen."
              />
              {precioCliente !== "" && (
                <InlineStack align="space-between">
                  <Text tone="subdued">Margen</Text>
                  <Text tone={Number(precioCliente) - e.total < 0 ? "critical" : "success"} fontWeight="semibold">
                    {formatPrice(Number(precioCliente) - e.total)}
                    {Number(precioCliente) > 0
                      ? ` (${Math.round(((Number(precioCliente) - e.total) / Number(precioCliente)) * 100)}%)`
                      : ""}
                  </Text>
                </InlineStack>
              )}
            </BlockStack>
          )}
          {!e.complete && (
            <Banner tone="warning">
              Falta:{" "}
              {missing
                .map(
                  (m) =>
                    ({
                      fuel_price: "precio de combustible",
                      km_per_liter: "km/L del modelo",
                      tolls: "casetas",
                      return_fare: "regreso del chofer (pon $0 si regresa con otro traslado)",
                    })[m] || m,
                )
                .join(", ")}
              . El total es un piso, no el costo esperado.{" "}
              {(missing.includes("fuel_price") || missing.includes("km_per_liter")) && (
                <Link url="/traslado_rates">Tarifas de Traslado</Link>
              )}
            </Banner>
          )}
        </BlockStack>
      </Card>
    );
  };

  const notEditable = editing && original && !EDITABLE_STATUSES.includes(original.status);
  // Chofer pay that will be saved: a hand-negotiated one is kept on edit.
  const choferPay =
    editing && original?.driver_extra != null &&
    Number(original.driver_extra) !== Number(original.suggested_extra)
      ? Number(original.driver_extra)
      : Number(estimate?.suggested_extra || 0);
  const secondaryError =
    secondaryId && secondaryAmount !== "" &&
    (Number(secondaryAmount) < 0 || Number(secondaryAmount) > choferPay)
      ? `No puede ser mayor al pago al chofer (${formatPrice(choferPay)})`
      : null;
  const canSave =
    !!(car && route && estimate && hasPermission("services.create")) && !notEditable && !secondaryError;

  if (editing && (loadError || notEditable))
    return (
      <AppWrapper>
        <Page title={`Editar traslado #${trasladoId}`} backAction={{ url: `/vehicle_transfers/${trasladoId}` }}>
          <Banner tone="warning">
            {loadError ||
              "Este traslado ya empezó o está cerrado; la cotización ya no se puede editar."}
          </Banner>
        </Page>
      </AppWrapper>
    );

  return (
    <AppWrapper>
      <Page
        title={editing ? `Editar traslado #${trasladoId}` : "Nuevo traslado"}
        backAction={{ url: editing ? `/vehicle_transfers/${trasladoId}` : "/vehicle_transfers" }}
        primaryAction={
          editing
            ? { content: "Guardar cambios", disabled: !canSave, loading: saving, onAction: () => save() }
            : {
                content: "Guardar y autorizar",
                disabled: !canSave,
                loading: saving,
                onAction: () => save("authorized"),
              }
        }
        secondaryActions={
          editing
            ? []
            : [
                {
                  content: "Guardar como cotización",
                  disabled: !canSave || saving,
                  onAction: () => save("draft"),
                },
              ]
        }
      >
        <BlockStack gap="400">
          {saveError && <Banner tone="critical">{saveError}</Banner>}
          <InlineGrid columns={{ xs: 1, md: ["twoThirds", "oneThird"] }} gap="400">
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="300">
                  <Text variant="headingMd">Unidad</Text>
                  {car ? (
                    <InlineStack gap="300" blockAlign="center" wrap={false}>
                      {car.model?.image_url && (
                        <img
                          src={car.model.image_url}
                          alt=""
                          style={{ width: 90, height: 60, objectFit: "contain" }}
                        />
                      )}
                      <BlockStack gap="100">
                        <Text fontWeight="semibold">{carTitle}</Text>
                        <Text tone="subdued">
                          {car.plate} · {car.model?.fuel?.name || "—"} ·{" "}
                          {car.model?.km_per_liter ? `${car.model.km_per_liter} km/L` : "sin km/L"}
                        </Text>
                        <InlineStack>
                          <Button variant="plain" onClick={() => setCar(null)}>
                            Cambiar
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </InlineStack>
                  ) : (
                    <form onSubmit={searchCar}>
                      <InlineStack gap="200" wrap={false}>
                        <div style={{ flexGrow: 1 }}>
                          <TextField
                            autoComplete="off"
                            placeholder="Placa o VIN"
                            value={plate}
                            onChange={(v) => setPlate(v.toUpperCase())}
                            error={carError}
                          />
                        </div>
                        <Button icon={SearchIcon} submit loading={carLoading} />
                      </InlineStack>
                    </form>
                  )}
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd">Ruta</Text>
                    {route && (
                      <Button
                        variant="plain"
                        icon={RefreshIcon}
                        loading={routeLoading}
                        onClick={() => fetchRoute(true)}
                      >
                        Recalcular con Google
                      </Button>
                    )}
                  </InlineStack>
                  <InlineGrid columns={2} gap="300">
                    <TextField
                      label="CP origen"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength={5}
                      value={originCp}
                      onChange={(v) => setOriginCp(v.replace(/\D/g, "").slice(0, 5))}
                      helpText={route?.origin_label}
                    />
                    <TextField
                      label="CP destino"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength={5}
                      value={destCp}
                      onChange={(v) => setDestCp(v.replace(/\D/g, "").slice(0, 5))}
                      helpText={route?.dest_label}
                    />
                  </InlineGrid>
                  {routeLoading && (
                    <InlineStack gap="200" blockAlign="center">
                      <Spinner size="small" />
                      <Text tone="subdued">Calculando ruta…</Text>
                    </InlineStack>
                  )}
                  {routeError && <Banner tone="critical">{routeError}</Banner>}
                  {route && !routeLoading && (
                    <BlockStack gap="300">
                      <InlineStack gap="200" blockAlign="center">
                        <Text fontWeight="semibold">
                          {route.origin_label} → {route.dest_label}
                        </Text>
                        <Badge>{`${route.km} km`}</Badge>
                        <Badge>{formatDuration(route.duration_min)}</Badge>
                        {route.cached && (
                          <Text tone="subdued" variant="bodySm">
                            (ruta guardada)
                          </Text>
                        )}
                      </InlineStack>
                      <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
                        <TextField
                          label="Kilómetros"
                          suffix="km"
                          autoComplete="off"
                          value={km}
                          readOnly
                        />
                        <TextField
                          label="Casetas"
                          type="number"
                          prefix="$"
                          autoComplete="off"
                          value={toll}
                          onChange={setToll}
                          helpText={
                            !route.toll_known
                              ? "Google no tiene el precio; captúralo"
                              : tollEdited
                                ? `Google: ${formatPrice(route.toll_mxn)}`
                                : null
                          }
                        />
                        <TextField
                          label="Regreso del chofer"
                          type="number"
                          prefix="$"
                          autoComplete="off"
                          value={returnFare}
                          onChange={setReturnFare}
                          placeholder="0 si regresa con otro traslado"
                        />
                        <TextField
                          label="Comida"
                          type="number"
                          prefix="$"
                          autoComplete="off"
                          value={comida}
                          onChange={setComida}
                          helpText={
                            route.duration_min != null
                              ? `Sugerido por ${formatDuration(route.duration_min)} de manejo`
                              : "Sugerido por distancia"
                          }
                        />
                      </InlineGrid>
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <Text variant="headingMd">Detalles (opcional)</Text>
                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                    <BlockStack gap="100">
                      <Select
                        label="Trasladista"
                        options={[
                          { label: "Sin asignar", value: "" },
                          ...trasladistas.map((t) => ({ label: t.name, value: String(t.id) })),
                        ]}
                        value={trasladistaId}
                        onChange={(v) => {
                          setTrasladistaId(v);
                          // The secondary can't be the main one, and needs one.
                          if (!v || v === secondaryId) {
                            setSecondaryId("");
                            setSecondaryAmount("");
                          }
                        }}
                      />
                      {trasladista && (
                        <InlineStack gap="200" blockAlign="center">
                          <ClabeBadge
                            status={trasladista.bank.clabe_status}
                            holderName={trasladista.bank.holder_name}
                          />
                          {licenseExpired && <Badge tone="critical">Licencia vencida</Badge>}
                        </InlineStack>
                      )}
                      {!trasladistas.length && (
                        <Text tone="subdued" variant="bodySm">
                          <Link url="/trasladistas">Da de alta trasladistas</Link> para asignarlos.
                        </Text>
                      )}
                    </BlockStack>
                    {trasladistaId && (
                      <BlockStack gap="100">
                        <Select
                          label="Trasladista secundario (opcional)"
                          options={[
                            { label: "Ninguno", value: "" },
                            ...trasladistas
                              .filter((t) => String(t.id) !== trasladistaId)
                              .map((t) => ({ label: t.name, value: String(t.id) })),
                          ]}
                          value={secondaryId}
                          onChange={(v) => {
                            setSecondaryId(v);
                            if (!v) setSecondaryAmount("");
                          }}
                        />
                        {secondaryId && (
                          <TextField
                            label="Le corresponde del pago al chofer"
                            type="number"
                            prefix="$"
                            autoComplete="off"
                            value={secondaryAmount}
                            onChange={setSecondaryAmount}
                            error={secondaryError}
                            helpText={
                              estimate && !secondaryError
                                ? `${trasladista?.name || "Principal"} recibe ${formatPrice(
                                    choferPay - Number(secondaryAmount || 0),
                                  )} + comida y reembolsos`
                                : null
                            }
                          />
                        )}
                      </BlockStack>
                    )}
                    <TextField
                      label="Fecha programada"
                      type="datetime-local"
                      autoComplete="off"
                      value={scheduledAt}
                      onChange={setScheduledAt}
                    />
                    <Select
                      label="TAG PASE (opcional)"
                      options={tagOptions(tags)}
                      value={paseTag}
                      onChange={setPaseTag}
                    />
                  </InlineGrid>
                  <TextField
                    label="Notas"
                    multiline={2}
                    autoComplete="off"
                    value={notes}
                    onChange={setNotes}
                    placeholder="Motivo, cliente, dirección exacta…"
                  />
                </BlockStack>
              </Card>
            </BlockStack>

            {estimateCard()}
          </InlineGrid>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default NewTraslado;
